import { FieldMapping } from '../services/fieldMapping.service';

/**
 * Utility functions for form components to handle field mappings and read-only state
 */

/**
 * Check if a field should be hidden based on field mappings
 */
export const isFieldHidden = (
  fieldName: string, 
  sectionName: string, 
  fieldMappings: FieldMapping[] = []
): boolean => {
  if (!fieldMappings || fieldMappings.length === 0) {
    return false;
  }
  const mapping = fieldMappings.find(
    (m) => m.form_field === `${sectionName}.${fieldName}`
  );
  return mapping ? mapping.is_hidden : false;
};

/**
 * Check if a field is required based on field mappings.
 * Returns false (not required) when no mappings exist — every field is optional by default.
 */
export const isFieldRequired = (
  fieldName: string,
  sectionName: string,
  fieldMappings: FieldMapping[] = []
): boolean => {
  if (!fieldMappings || fieldMappings.length === 0) {
    return false;
  }
  const mapping = fieldMappings.find(
    (m) => m.form_field === `${sectionName}.${fieldName}`
  );
  return mapping ? (mapping.is_required && !mapping.is_hidden) : false;
};

/**
 * Get common props for form fields (disabled state, etc.)
 */
export const getFieldProps = (readOnly: boolean, additionalProps = {}) => ({
  disabled: readOnly,
  ...additionalProps,
});

/**
 * Create a change handler that respects read-only mode
 */
export const createChangeHandler = (
  readOnly: boolean,
  formData: any,
  setFormData: (data: any) => void,
  onChange: (data: any) => void
) => {
  return (field: string, value: any) => {
    if (readOnly) return; // Prevent changes in read-only mode
    
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    onChange(updatedData);
  };
};

/**
 * Get section title with read-only indicator
 */
export const getSectionTitle = (title: string, readOnly: boolean) => ({
  title,
  readOnlyIndicator: readOnly ? {
    component: 'span' as const,
    variant: 'body2' as const,
    color: 'warning.main' as const,
    sx: { ml: 2, fontStyle: 'italic', fontWeight: 'bold' },
    text: '(Read Only - View Only)'
  } : null
});

/**
 * Get container styles for read-only mode
 */
export const getContainerStyles = (readOnly: boolean) => ({
  opacity: readOnly ? 0.8 : 1
});
